import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';

import { db } from '../../firebase.js';
import SignupLinkDateTimeField from '../../components/SignupLinkDateTimeField/SignupLinkDateTimeField.jsx';
import SignupLinkShare from '../../components/SignupLinkShare/SignupLinkShare.jsx';
import {
    copenhagenInputToDate,
    dateToCopenhagenInput,
    formatCopenhagenDateTime,
    signupLinkDisplayState,
} from '../../common/signupLink.js';
import {
    SIGNUP_TARGETS,
    signupLinkLabel,
    signupLinkUrl,
    subscribeToAllSignupLinks,
    updateSignupLink,
} from '../../common/signupLinkApi.js';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const SHOWN_STATES = new Set(['scheduled', 'active', 'full']);

function formatDuration(ms) {
    const days = Math.floor(ms / DAY_MS);
    const hours = Math.floor((ms % DAY_MS) / HOUR_MS);
    const minutes = Math.max(1, Math.floor((ms % HOUR_MS) / MINUTE_MS));
    if (days > 0) return `${days} d ${hours} t`;
    if (hours > 0) return `${hours} t ${minutes} min`;
    return `${minutes} min`;
}

const getDocData = async (path) => {
    const snapshot = await getDoc(doc(db, ...path));
    return snapshot.exists() ? snapshot.data() : null;
};

// Who signed up, and the settings to extend the link, in a window of their own so the panel
// itself stays the same size.
const SignupLinkOverviewDialog = ({ link, company, title, userNames, onClose }) => {
    const [end, setEnd] = useState(dateToCopenhagenInput(link.end_date));
    const [maxUsers, setMaxUsers] = useState(String(link.max_users));
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const used = link.users_signed_up.length;

    const save = async () => {
        const endDate = end ? copenhagenInputToDate(end) : null;
        const nextMaxUsers = Number(maxUsers);
        if (!endDate || endDate <= new Date() || endDate <= link.start_date) {
            setError('Slut skal ligge i fremtiden og efter start.');
            return;
        }
        if (!Number.isInteger(nextMaxUsers) || nextMaxUsers < Math.max(used, 1)) {
            setError(`Antal brugere skal være mindst ${Math.max(used, 1)}.`);
            return;
        }

        setSaving(true);
        try {
            await updateSignupLink(link.companyId, link.id, { end_date: endDate, max_users: nextMaxUsers });
            onClose();
        } catch (saveError) {
            console.error('Could not update sign-up link', saveError);
            setError('Linket kunne ikke opdateres.');
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} className="signup-overview-dialog" maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <h3>Link</h3>
                {company
                    ? (
                        <SignupLinkShare
                            url={signupLinkUrl(company.name || '', link.companyId, link.id)}
                            fileName={`${company.name || link.companyId} ${signupLinkLabel(link)}`}
                        />
                    )
                    : <span className="signup-overview-note">Henter virksomhed…</span>}

                <h3>Tilmeldte ({used} / {link.max_users})</h3>
                <div className="signup-overview-users">
                    {used === 0
                        ? <span className="signup-overview-note">Ingen har tilmeldt sig endnu.</span>
                        : link.users_signed_up.map((signup) => (
                            <div key={signup.uid} className="signup-overview-user">
                                <strong>{userNames[signup.uid] || signup.email}</strong>
                                <span className="signup-overview-note">
                                    {signup.email} · {formatCopenhagenDateTime(signup.signed_up_at)}
                                </span>
                            </div>
                        ))}
                </div>

                <h3>Indstillinger</h3>
                <div className="signup-overview-settings">
                    <SignupLinkDateTimeField
                        label="Slut (dansk tid)"
                        value={end}
                        onChange={setEnd}
                    />
                    <TextField
                        label="Antal brugere"
                        type="number"
                        value={maxUsers}
                        onChange={(event) => setMaxUsers(event.target.value)}
                        size="small"
                        inputProps={{ min: Math.max(used, 1), step: 1 }}
                    />
                </div>
                {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Luk</Button>
                <Button variant="contained" onClick={save} disabled={saving}>Gem</Button>
            </DialogActions>
        </Dialog>
    );
};

const SignupLinkPanel = ({ link, company, userNames, now }) => {
    const [overviewOpen, setOverviewOpen] = useState(false);
    const state = signupLinkDisplayState(link, now);
    const used = link.users_signed_up.length;
    const title = `${company?.name || link.companyId} · ${signupLinkLabel(link)}`;

    return (
        <div className="card-wrapper signup-panel-wrapper">
            <div className="login-container card-container signup-panel">
                <div className="card-header">
                    <div className="card-header-title">
                        {title}
                    </div>
                </div>
                <div className="card-body signup-panel-body">
                    <div className="signup-panel-badges">
                        <span className="signup-panel-badge">
                            Tilmelding til {SIGNUP_TARGETS[link.target]?.label || link.target}
                        </span>
                        {state === 'full' && <span className="signup-panel-badge signup-panel-badge-full">Fuld</span>}
                        {company && !company.waste_receiver && (
                            <span className="signup-panel-badge signup-panel-badge-warning">
                                Ingen affaldsmodtager – linket virker ikke
                            </span>
                        )}
                    </div>

                    <div className="signup-panel-stats">
                        <div>
                            <div className="signup-panel-stat-label">
                                {state === 'scheduled' ? 'Starter om' : 'Tid tilbage'}
                            </div>
                            <div className="signup-panel-stat-value">
                                {formatDuration(state === 'scheduled' ? link.start_date - now : link.end_date - now)}
                            </div>
                            <div className="signup-panel-stat-note">
                                {formatCopenhagenDateTime(link.start_date)} – {formatCopenhagenDateTime(link.end_date)}
                            </div>
                        </div>
                        <div>
                            <div className="signup-panel-stat-label">Tilmeldte</div>
                            <div className="signup-panel-stat-value">{used} / {link.max_users}</div>
                        </div>
                    </div>

                    <div className="signup-panel-actions">
                        <button type="button" className="aris-button-primary" onClick={() => setOverviewOpen(true)}>
                            Oversigt
                        </button>
                    </div>
                </div>
            </div>

            {overviewOpen && (
                <SignupLinkOverviewDialog
                    link={link}
                    company={company}
                    title={title}
                    userNames={userNames}
                    onClose={() => setOverviewOpen(false)}
                />
            )}
        </div>
    );
};

// For admins: a panel for each sign-up link that is running or about to, showing how it is
// being used, with an overview window to see who signed up and extend it. Links that are over
// live in the Setup page.
const SignupLinkPanels = () => {
    const [links, setLinks] = useState([]);
    const [companies, setCompanies] = useState({});
    const [userNames, setUserNames] = useState({});
    const [now, setNow] = useState(() => new Date());

    useEffect(() => subscribeToAllSignupLinks(
        setLinks,
        (error) => console.error('Could not load sign-up links', error),
    ), []);

    // Keeps "time left" honest, and moves links between states, while the page stays open.
    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), MINUTE_MS);
        return () => clearInterval(interval);
    }, []);

    const shownLinks = links
        .filter((link) => SHOWN_STATES.has(signupLinkDisplayState(link, now)))
        .sort((left, right) => left.end_date - right.end_date);

    const companyIds = [...new Set(shownLinks.map((link) => link.companyId))].sort().join(',');
    const userIds = [...new Set(shownLinks.flatMap((link) => link.users_signed_up.map((signup) => signup.uid)))]
        .sort()
        .join(',');

    useEffect(() => {
        if (!companyIds) return;
        let cancelled = false;
        Promise.all(companyIds.split(',').map(async (id) => [id, await getDocData(['companies', id])]))
            .then((entries) => {
                if (!cancelled) setCompanies(Object.fromEntries(entries));
            })
            .catch((error) => console.error('Could not load companies for sign-up links', error));
        return () => {
            cancelled = true;
        };
    }, [companyIds]);

    useEffect(() => {
        if (!userIds) return;
        let cancelled = false;
        Promise.all(userIds.split(',').map(async (id) => [id, (await getDocData(['users', id]))?.name]))
            .then((entries) => {
                if (!cancelled) setUserNames(Object.fromEntries(entries.filter(([, name]) => name)));
            })
            .catch((error) => console.error('Could not load names of signed-up users', error));
        return () => {
            cancelled = true;
        };
    }, [userIds]);

    return shownLinks.map((link) => (
        <SignupLinkPanel
            key={`${link.companyId}-${link.id}`}
            link={link}
            company={companies[link.companyId]}
            userNames={userNames}
            now={now}
        />
    ));
};

export default SignupLinkPanels;
